<template>
  <div class="sb-textfield" :class="wrapperClass" @click="handleAutoFocus">
    <label
      v-if="label && !isInlineLabel"
      :for="`${uid}-input`"
      class="sb-textfield__label"
    >
      {{ label }}
      <span v-if="required" class="sb-textfield__required" aria-hidden="true">
        *
      </span>
      <SbIcon
        v-if="helpText"
        :id="`${uid}-help-text`"
        v-tooltip="helpText"
        name="circle-help"
      />
    </label>
    <div
      class="sb-textfield__inner"
      :class="{
        'sb-textfield__inner--error': error,
      }"
    >
      <span
        v-if="prefix || $slots.prefix"
        class="sb-textfield__prefix"
        @click.stop
      >
        <template v-if="!$slots.prefix">{{ prefix }}</template>
        <slot name="prefix" />
      </span>

      <slot name="icons">
        <SbIcon
          v-if="iconLeft"
          :name="iconLeft"
          :color="iconColor"
          :data-testid="`${baseDataTestId}-icon-click`"
        />
      </slot>

      <label
        v-if="isInlineLabel && label"
        :for="`${uid}-input`"
        class="sb-textfield__inline-label"
        :data-testid="baseDataTestId"
      >
        {{ label }}:
      </label>

      <slot name="input">
        <input
          v-if="type !== 'textarea'"
          :id="label ? `${uid}-input` : id"
          ref="textfield"
          v-model="computedValue"
          v-bind="inputAttrs"
          v-maska="mask"
          :aria-describedby="ariaDescribedby"
          :aria-disabled="disabled"
          :aria-invalid="hasError"
          :aria-label="label"
          :aria-required="required"
          :autocomplete="autocomplete"
          :inputmode="['number', 'tel'].includes(type) ? 'numeric' : 'text'"
          :minlength="minlength"
          :name="name"
          :pattern="computedPattern"
          :placeholder="placeholder"
          :readonly="readonly || disabled"
          :required="required"
          :type="internalType"
          aria-live="polite"
          class="sb-textfield__input"
          :class="{
            'sb-textfield__input--error': error,
          }"
          :dir="dir"
          :aria-busy="ariaBusy"
          :step="step"
          @focus="handleFocusInput"
          @blur="handleBlurInput"
          @keydown="handleKeyDownInput"
          @keypress="handleKeyPressInput"
          @keyup="handleKeyUpInput"
          @mousedown="handleMouseDownInput"
          @mouseup="handleMouseUpInput"
        />

        <textarea
          v-else
          :id="label ? `${uid}-input` : id"
          ref="textfield"
          v-model="computedValue"
          v-bind="inputAttrs"
          :aria-describedby="ariaDescribedby"
          :aria-disabled="disabled"
          :aria-invalid="hasError"
          :aria-label="label"
          :aria-required="required"
          :minlength="minlength"
          :maxlength="maxlength"
          :name="name"
          :placeholder="placeholder"
          :readonly="readonly || disabled"
          :required="required"
          :rows="rows"
          :dir="dir"
          aria-live="polite"
          class="sb-textfield__input sb-textfield__textarea"
          :aria-busy="ariaBusy"
          @focus="handleFocusInput"
          @blur="handleBlurInput"
          @keydown="handleKeyDownInput"
          @keypress="handleKeyPressInput"
          @keyup="handleKeyUpInput"
          @mousedown="handleMouseDownInput"
          @mouseup="handleMouseUpInput"
        />
      </slot>

      <slot />

      <SbIconButton
        v-if="hasValue && clearable"
        :label="clearIconButtonLabel"
        size="small"
        class="sb-textfield__clear-button"
        :data-testid="`${baseDataTestId}-icon-clear`"
        @click="handleClearableClick"
      >
        <SbIcon name="circle-x" :color="iconColor" />
      </SbIconButton>

      <SbIconButton
        v-if="iconRight && iconRightInteractive"
        :data-testid="`${baseDataTestId}-icon-click`"
        :label="iconRightLabel"
        size="small"
        type="button"
        @click="handleIconButtonClick"
      >
        <SbIcon :name="internalIconRight" :color="iconColor" />
      </SbIconButton>

      <SbIcon
        v-if="(iconRight && !iconRightInteractive) || statusIcon.name"
        :name="statusIcon.name || iconRight"
        :color="statusIcon.color || iconColor"
        :data-testid="`${baseDataTestId}-icon`"
      />

      <span
        v-if="suffix || $slots.suffix"
        :class="{
          'sb-textfield__suffix': true,
          'sb-textfield__suffix--custom': $slots.suffix,
        }"
        @click.stop
      >
        <template v-if="!$slots.suffix">{{ suffix }}</template>
        <slot v-else name="suffix" @keydown.prevent />
      </span>
    </div>

    <div
      v-if="ariaDescribedby"
      id="textfield-messages"
      class="sb-textfield__bottom"
    >
      <span v-if="showError || !!description" class="sb-textfield__messages">
        <p
          v-if="showError"
          :id="`${uid}-error`"
          class="sb-textfield__message sb-textfield__message--error"
          :class="messageClass"
        >
          {{ errorMessage }}
        </p>
        <p
          v-else-if="!!description"
          :id="`${uid}-description`"
          class="sb-textfield__message sb-textfield__message--description"
          :class="messageClass"
        >
          {{ description }}
        </p>
      </span>

      <span
        v-if="showCounter"
        :id="`${uid}-counter`"
        class="sb-textfield__counter"
        :class="counterClass"
      >
        {{ valueLength }}/{{ maxLengthParsed }}
      </span>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  ref,
  watch,
  onMounted,
  nextTick,
  useAttrs,
  useId,
  type InputTypeHTMLAttribute,
  type InputHTMLAttributes,
} from 'vue'
import { maska as vMaska } from 'maska'
import { Tooltip as vTooltip } from '../../directives'

import SbIcon, { type IconName } from '../Icon'
import SbIconButton from '../IconButton'

import type { IconColor } from '../Icon/types'
import type { SbTextFieldModelValue } from './types'

defineOptions({
  inheritAttrs: false,
})

defineExpose({
  focus,
})

const attrs = useAttrs()

type TextFieldProps = {
  /**
   * Textfield label
   */
  label?: string
  /**
   * This will eventually be deprecated. We no longer will be using inline labels in our designs.
   * @deprecated
   */
  isInlineLabel?: boolean
  /**
   * Textfield wrapper class
   */
  placeholder?: string
  /**
   * Textfield type. It accepts the input types, like text, password, number, and textarea.
   */
  type?: InputTypeHTMLAttribute
  /**
   * Textfield warning state. Indicates that the textfield has a warning state. It shows a warning icon.
   */
  warn?: boolean
  /**
   * Textfield error state. Indicates that the textfield has an error state. It shows an error icon.
   */
  error?: boolean
  /**
   * Textfield error message. The message that will be shown when the textfield has an error state.
   */
  errorMessage?: string
  /**
   * Textfield description. A description that will be shown below the textfield.
   */
  description?: string
  /**
   * Textfield help text. It shows in a tooltip when the user hovers the help icon.
   */
  helpText?: string
  /**
   * Textfield prefix text. It will be shown before the input.
   */
  prefix?: string
  /**
   * Textfield suffix text. It will be shown after the input.
   */
  suffix?: string
  /**
   * Textfield disabled state. The disable state does not remove the focus of the input
   */
  disabled?: boolean
  /**
   * Disables interaction with the field.
   */
  readonly?: boolean
  /**
   * Textfield required state
   */
  required?: boolean
  /**
   * Indicates the icon name placed on the left side of the textfield
   */
  iconLeft?: IconName
  /**
   * Indicates the icon name placed on the right side of the textfield
   */
  iconRight?: IconName
  /**
   * Indicates if the iconRight is interactive. When it is true, a SbIconButton is used instead of SbIcon
   */
  iconRightInteractive?: boolean
  /**
   * Indicates the label of the iconRight when it is interactive. This is mandatory when the iconRight is interactive, as a label needs to be associated with the button.
   */
  iconRightLabel?: string
  /**
   * Indicates the color of the icon. Applies to both iconLeft and iconRight
   */
  iconColor?: IconColor
  /**
   * Indicates if the textfield has an icon button to clear its value. The clear icon button is shown when the textfield has a value
   */
  clearable?: boolean
  /**
   * Indicates the label of the clear icon button
   */
  clearIconButtonLabel?: string
  /**
   * Textfield value
   */
  modelValue?: SbTextFieldModelValue
  /**
   * Textfield mask. It uses the maska library to mask the input value. This property is used as the parameter of the maska directive
   */
  mask?: string
  /**
   * Textfield pattern. It is used to validate the input value. This property is used as the parameter of the pattern attribute of the input element
   */
  pattern?: string
  /**
   * Textfield maxlength. This property does not prevent the user from input more data then the set maximum, but shows the counter of the remaining and exceeded characters.
   */
  maxlength?: string | number
  /**
   * Textfield minlength. It is used to validate the minimum number of characters that the user can input. This property is used as the parameter of the minlength attribute of the input element
   */
  minlength?: string | number
  /**
   * Textfield id attribute
   */
  id?: string
  /**
   * Textfield name attribute. This property is used as the parameter of the name attribute of the input element
   */
  name?: string
  /**
   * Textfield autocomplete attribute. This property is used as the parameter of the autocomplete attribute of the input element
   */
  autocomplete?: string
  /**
   * Textfield useAutofocus attribute. This property is used to focus on the input.
   */
  useAutofocus?: boolean
  /**
   * Textfield step attribute. This property is used as the parameter of the step attribute of the input element
   */
  step?: string | number
  /**
   * Textfield wrapper class. This property is used to add a custom class to the wrapper div of the textfield
   */
  wrapperClass?: string
  /**
   * Textfield aria-busy attribute. This property is used as the parameter of the aria-busy attribute of the input element. This property was added because there are some components that need to indicate that they are loading data, like the SbMinibrowser
   */
  ariaBusy?: boolean
  /**
   * Textfield rows attribute. This property is used to define the number of visible text lines in a textarea element.
   * This property was added to support textarea height control and resolve type errors for the `rows` attribute.
   */
  rows?: number

  /**
   * Textfield dir attribute. This property is used as the parameter of the dir attribute of the input element
   */
  dir?: HTMLInputElement['dir']

  counterClass?: string
  messageClass?: string
}

const props = withDefaults(defineProps<TextFieldProps>(), {
  label: undefined,
  isInlineLabel: false,
  placeholder: undefined,
  type: 'text',
  warn: false,
  error: false,
  errorMessage: undefined,
  description: undefined,
  helpText: undefined,
  prefix: undefined,
  suffix: undefined,
  disabled: false,
  readonly: false,
  required: false,
  iconLeft: undefined,
  iconRight: undefined,
  iconRightInteractive: false,
  iconRightLabel: 'Show/Hide password',
  iconColor: 'primary',
  clearable: false,
  clearIconButtonLabel: 'Clear field value',
  modelValue: undefined,
  mask: undefined,
  pattern: undefined,
  maxlength: undefined,
  minlength: undefined,
  id: undefined,
  name: undefined,
  autocomplete: undefined,
  useAutofocus: false,
  step: undefined,
  wrapperClass: undefined,
  ariaBusy: false,
  rows: undefined,
})

const emit = defineEmits<{
  (e: 'blur', value: string): void
  (e: 'clear', value: SbTextFieldModelValue): void
  (e: 'focus', event: Event): void
  (e: 'icon-click'): void
  (e: 'keydown', event: KeyboardEvent): void
  (e: 'keypress', event: KeyboardEvent): void
  (e: 'keyup', event: KeyboardEvent): void
  (e: 'mousedown', event: MouseEvent): void
  (e: 'mouseup', event: MouseEvent): void
  (e: 'update:modelValue', value: string): void
}>()

const internalValue = ref<SbTextFieldModelValue>(props.modelValue)
const internalType = ref<InputTypeHTMLAttribute>(
  props.type === 'number' ? 'text' : props.type,
)
const internalIconRight = ref<IconName | undefined>(props.iconRight)
const internalError = ref<boolean>(false)

const textfield = ref<HTMLInputElement | HTMLTextAreaElement | null>(null)

const uid = computed((): string => `textfield-${useId()}`)

const computedValue = computed<SbTextFieldModelValue, string>({
  get() {
    return internalValue.value
  },
  set(newValue): void {
    internalValue.value = newValue

    if (computedValue.value) {
      internalError.value = !isInputValid()
    } else {
      internalError.value = false
    }

    emit('update:modelValue', newValue)
  },
})

const inputAttrs = computed<InputHTMLAttributes>(() => {
  if (props.label) {
    const { id: _, ...rest } = attrs
    return rest
  } else {
    return attrs
  }
})

const hasError = computed((): boolean => props.error || internalError.value)

const statusIcon = computed(
  (): { name: IconName | undefined; color: IconColor | null } => {
    if (hasError.value) {
      return { name: 'octagon-alert', color: 'danger' }
    }

    if (props.warn) {
      return { name: 'triangle-alert', color: 'warning' }
    }

    return { name: undefined, color: null }
  },
)

const showCounter = computed((): boolean => !!props.maxlength)

const ariaDescribedby = computed((): string => {
  const describedBy = []

  if (props.helpText) {
    describedBy.push(`${uid.value}-help-text`)
  }

  if (props.error && props.errorMessage) {
    describedBy.push(`${uid.value}-error`)
  } else if (props.description) {
    describedBy.push(`${uid.value}-description`)
  }

  if (showCounter.value) {
    describedBy.push(`${uid.value}-counter`)
  }

  return describedBy.join(' ')
})

const computedPattern = computed((): string | undefined => {
  if (props.pattern) {
    return props.pattern
  }

  if (props.maxlength) {
    return `^.{0,${props.maxlength}}`
  }

  if (props.type === 'number') {
    return '^[0-9]*$'
  }

  return undefined
})

const baseDataTestId = computed<string>(() => {
  const dataTestid = attrs['data-testid']
  return typeof dataTestid === 'string' ? dataTestid : 'sb-textfield'
})

const hasValue = computed(
  (): boolean =>
    computedValue.value !== null &&
    computedValue.value !== undefined &&
    // TODO this is insane
    ('' + computedValue.value).length > 0,
)

const showError = computed(
  (): boolean => props.error && Boolean(props.errorMessage),
)

const maxLengthParsed = computed((): number =>
  props.maxlength ? Number(props.maxlength) : 0,
)

const computedValueLength = computed((): number => {
  if (computedValue.value === '' || typeof computedValue.value !== 'string') {
    return 0
  }

  return computedValue.value.length
})

const valueLength = computed((): number =>
  showCounter.value ? computedValueLength.value : 0,
)

watch(
  () => props.useAutofocus,
  (val: boolean): void => {
    if (val) {
      handleAutoFocus()
    }
  },
)

watch(
  () => props.modelValue,
  (val: SbTextFieldModelValue): void => {
    internalValue.value = val
  },
)

onMounted((): void => {
  if (props.useAutofocus) {
    handleAutoFocus()
  }
})

function isInputValid(): boolean {
  if (!textfield.value) {
    return false
  }
  return textfield.value?.checkValidity()
}

function handleAutoFocus(): void {
  if (!props.iconRightInteractive) {
    focus()
  }
}

function handleShowHidePassword(): void {
  internalType.value = internalType.value === 'password' ? 'text' : 'password'
  internalIconRight.value =
    internalIconRight.value === 'eye' ? 'eye-off' : 'eye'
}

function handleIconButtonClick(): void {
  if (props.type === 'password') {
    handleShowHidePassword()
  } else {
    emit('icon-click')
  }
}

function handleClearableClick(): void {
  const previousValue = computedValue.value
  computedValue.value = ''
  emit('clear', previousValue)
}

function handleFocusInput(e: Event): void {
  emit('focus', e)
}

function handleBlurInput(e: Event): void {
  if (hasValue.value) {
    internalError.value = !isInputValid()
  } else {
    internalError.value = false
  }
  emit('blur', (e.target as HTMLInputElement)?.value || '')
}

function handleKeyDownInput(e: KeyboardEvent): void {
  emit('keydown', e)
}

function handleKeyPressInput(e: KeyboardEvent): void {
  emit('keypress', e)
}

function handleKeyUpInput(e: KeyboardEvent): void {
  emit('keyup', e)
}

function handleMouseDownInput(e: MouseEvent): void {
  emit('mousedown', e)
}

function handleMouseUpInput(e: MouseEvent): void {
  emit('mouseup', e)
}

function focus(): void {
  nextTick(() => {
    textfield.value?.focus()
  })
}
</script>
